import { Box, CircularProgress } from '@mui/material';
import { createFileRoute, Outlet, redirect } from '@tanstack/react-router';
import { AuthError, RouteErrorBoundary } from 'components/route-errors';
import type { JSX } from 'react';
import { authSearchSchema } from 'router/schemas';
import { initSession } from 'services/session';
import { redirectToLumiNext, shouldHandOffToLumiNext } from 'utils';

/**
 * Pending component shown during beforeLoad async operation
 */
const PendingLoader = (): JSX.Element => (
  <Box height="100vh" display="flex" justifyContent="center" alignItems="center">
    <CircularProgress size={60} />
  </Box>
);

export const Route = createFileRoute('/_authenticated')({
  // Validate search params with Zod schema
  validateSearch: authSearchSchema,

  // Session initialization happens before React renders
  beforeLoad: async ({ location, search, preload }) => {
    const result = await initSession({
      searchParams: search,
      pathname: location.pathname,
    });

    // Production cutover: hand any authenticated access off to lumi-next
    // (account.lumi-app.co). This also covers auto-login via a stored userId or
    // a ?orderId= URL param — initSession runs first so we can forward the
    // resolved user id for seamless auto-login on the new app. The returned
    // promise never resolves, keeping this route pending (loader shown) while
    // the browser navigates away. Gated on !preload so hover-preload
    // (defaultPreload: 'intent') never triggers the navigation side effect.
    if (!preload && shouldHandOffToLumiNext()) {
      const userId = result.userId ?? search.orderId ?? search.orderid ?? null;
      return redirectToLumiNext({ pathname: location.pathname, userId });
    }

    // Redirect to sign-in if not logged in
    if (!result.isUserLoggedIn) {
      throw redirect({ to: '/sign-in' });
    }

    // Return context data for child routes if needed
    return { userId: result.userId };
  },

  // Show loader during beforeLoad execution
  pendingComponent: PendingLoader,

  // Error handling - show auth error for session issues, generic error for others
  errorComponent: ({ error, reset }) => {
    const isAuthError =
      error?.message?.toLowerCase().includes('session') ||
      error?.message?.toLowerCase().includes('auth') ||
      error?.message?.toLowerCase().includes('unauthorized');

    if (isAuthError) {
      return <AuthError error={error} reset={reset} />;
    }
    return <RouteErrorBoundary error={error} reset={reset} />;
  },

  component: AuthenticatedLayout,
});

/**
 * Authenticated layout component.
 * At this point, session is guaranteed to be initialized
 * and user is guaranteed to be logged in (otherwise redirected in beforeLoad).
 */
function AuthenticatedLayout(): JSX.Element {
  return <Outlet />;
}
